<script setup lang="ts">
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { getToolPartName } from 'utils'
    import { isVoiceRenderAck } from 'models'
    import { CONFIRM_TOOL_NAME } from './utils'
    import { toolApprovalPreviews } from './toolApprovalPreviews'

    /**
     * The single confirmation card of the chat. It renders two things that are
     * the same question for the user:
     *
     * - **approval**: a tool call the backend suspended waiting for consent
     *   (`approval-requested`), plus its resolved states so the decision stays
     *   visible in the transcript;
     * - **confirm**: a `requestConfirm` call, the client-side tool the model uses
     *   to have an action it described approved. Same layout, its own wording
     *   (Proceed / Cancel, as on the messaging channels).
     *
     * The body comes from `toolApprovalPreviews[toolName]` when the tool has a
     * dedicated preview (localized, human copy: e.g. `sendEmail` will reuse the
     * email renderer); tools without one show only the localized prompt, never
     * a raw echo of the call input. Adding a preview never requires touching
     * this component.
     */
    const props = defineProps<{
        part: unknown
        /**
         * Whether the user can still answer. False in read-only views (chat
         * logs) and, for approvals, once the conversation has moved past the
         * request: the backend closes an abandoned approval as denied, so
         * answering it later would not run the tool.
         */
        actionable?: boolean
        /**
         * The verdict given ON A CALL, when there is one. A confirm answered
         * during a call travels back as a spoken message and its part stays
         * on the render ack, so the outcome only exists client-side: this is
         * what closes the card and names the outcome instead of leaving the
         * buttons alive (or worse, showing the answer as "expired").
         */
        callVerdict?: boolean
    }>()

    const emit = defineEmits<{
        respond: [approved: boolean]
    }>()

    const { t: $t } = useI18n({ useScope: 'global' })

    const toolPart = computed(
        () =>
            props.part as {
                type?: string
                state?: string
                toolCallId?: string
                input?: unknown
                output?: unknown
                approval?: { id?: string; approved?: boolean; reason?: string }
            },
    )

    const toolName = computed(() =>
        getToolPartName(props.part as Parameters<typeof getToolPartName>[0]),
    )

    const isConfirm = computed(() => toolName.value === CONFIRM_TOOL_NAME)

    /**
     * True while the request still waits for an answer. A confirm drawn
     * during a CALL arrives already resolved with the note that it was drawn
     * (see `isVoiceRenderAck`, same rule as the forms): the note is not the
     * user's verdict, so the card keeps its buttons.
     */
    const isOpen = computed(() =>
        isConfirm.value
            ? (toolPart.value.state === 'input-available' ||
                  isVoiceRenderAck(toolPart.value.output)) &&
              props.callVerdict === undefined
            : toolPart.value.state === 'approval-requested',
    )

    /** Open and answerable: the only case where the buttons act. */
    const isPending = computed(() => isOpen.value && props.actionable === true)

    /** Open but not answerable: shown as an expired request. */
    const isExpired = computed(() => isOpen.value && props.actionable !== true)

    const isApproved = computed(() =>
        isConfirm.value
            ? toolPart.value.output === 'confirmed' ||
              props.callVerdict === true
            : toolPart.value.approval?.approved === true,
    )

    const isDenied = computed(() =>
        isConfirm.value
            ? toolPart.value.output === 'cancelled' ||
              props.callVerdict === false
            : toolPart.value.approval?.approved === false ||
              toolPart.value.state === 'output-denied',
    )

    const preview = computed(() => toolApprovalPreviews[toolName.value])

    /**
     * Once the request is answered (or expired) the card slims down: prompt
     * and buttons disappear and this single icon + text line tells the
     * outcome. A confirm has its own wording (the model described an action,
     * no tool ran on its behalf).
     */
    const outcome = computed(() => {
        if (isExpired.value) {
            return { icon: 'ri:time-line', message: $t('toolApproval.expired') }
        }
        if (isApproved.value) {
            return {
                icon: 'ri:check-line',
                message: $t(
                    isConfirm.value
                        ? 'toolApproval.confirmed'
                        : 'toolApproval.approved',
                ),
            }
        }
        if (isDenied.value) {
            return {
                icon: 'ri:close-line',
                message: $t(
                    isConfirm.value
                        ? 'toolApproval.cancelled'
                        : 'toolApproval.denied',
                ),
            }
        }
        return undefined
    })

    /**
     * Resolved and nothing to preview: all the card would frame is the
     * outcome line, so the frame goes too and only the line stays in the
     * transcript.
     */
    const isBare = computed(() => Boolean(outcome.value) && !preview.value)
</script>

<template>
    <!-- Plain block container, NOT flex-col: vv-button-group is inline-flex
         with justify-content center, and a stretched flex child would center
         the buttons instead of keeping them left like the original confirm
         card. -->
    <!-- transition-all eases the frame away (padding, border and background
         to zero) when the resolved card goes bare. -->
    <div
        class="w-full overflow-hidden transition-all duration-300"
        :class="{
            'bg-surface p-sm rounded-xl border border-surface-3': !isBare,
        }">
        <!-- A confirm carries its own sentence from the model; an approval gets
             a generic one. The operation itself is never named: the user sees
             only what is about to happen (the preview), not how. -->
        <!-- The ask reads wrong on a resolved request: once answered, the
             outcome line alone carries the whole story. -->
        <transition>
            <p
                v-if="!isConfirm && isOpen"
                class="text-12 text-word-3 m-0 mb-xs">
                {{ $t('toolApproval.prompt') }}
            </p>
        </transition>

        <component :is="preview" v-if="preview" :part :input="toolPart.input" />

        <!-- Buttons only while the answer is still awaited (disabled while the
             confirm input streams in). Once there is an outcome they leave the
             card, so the transcript keeps a slim record instead of a dead
             control. -->
        <transition mode="out-in">
            <VvButtonGroup v-if="!outcome">
                <VvButton
                    modifiers="primary-small"
                    :disabled="!isPending"
                    icon="ri:check-line"
                    :label="$t('action.proceed')"
                    @click.stop="emit('respond', true)" />
                <VvButton
                    modifiers="action-quiet-small"
                    :disabled="!isPending"
                    icon="ri:close-line"
                    :label="$t('action.cancel')"
                    @click.stop="emit('respond', false)" />
            </VvButtonGroup>

            <!-- The stored denial reason is model-facing copy (and English):
                 the card always shows the localized message instead. -->
            <p
                v-else
                class="flex items-center gap-4 text-12 text-word-3 m-0"
                :class="{ 'mt-xs': preview }">
                <VvIcon :name="outcome.icon" class="shrink-0" />
                {{ outcome.message }}
            </p>
        </transition>
    </div>
</template>
